import { useState } from "react";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { Tooltip } from "../../../components/Tooltip";
import {
  formatMoneyAmountInputValue,
  isValidMoneyAmountInputDraft,
  moneyAmountInputToMinor,
} from "../../../utils/parseMoneyAmountInput";

const cellDisabledStyles = `
  cursor: not-allowed;
  opacity: 0.75;
  background-color: ${colors.backgroundDark};
`;

export const CellInput = styled.input<{ $dirty?: boolean; $disabled?: boolean }>`
  width: 100%;
  padding: ${spacing.x05}px ${spacing.x1}px;
  border: 1px solid ${({ $dirty }) => ($dirty ? colors.warning : "transparent")};
  border-radius: 4px;
  background-color: ${({ $dirty }) =>
    $dirty ? colors.warningBackground : "transparent"};
  font-size: 14px;
  color: ${colors.text};

  &:hover:not(:disabled) {
    border-color: ${({ $dirty }) => ($dirty ? colors.warning : colors.border)};
  }

  &:focus:not(:disabled) {
    outline: none;
    border-color: ${colors.primary};
    background-color: ${colors.backgroundLight};
  }

  &:disabled {
    ${cellDisabledStyles}
  }
`;

const SELECT_CHEVRON = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%233a4864' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`;

export const CellSelect = styled.select<{ $dirty?: boolean; $disabled?: boolean }>`
  width: 100%;
  appearance: none;
  padding: ${spacing.x05}px ${spacing.x3}px ${spacing.x05}px ${spacing.x1}px;
  background-repeat: no-repeat;
  background-position: right ${spacing.x1}px center;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background-image: ${SELECT_CHEVRON};
  }
  border: 1px solid ${({ $dirty }) => ($dirty ? colors.warning : "transparent")};
  border-radius: 4px;
  background-color: ${({ $dirty }) =>
    $dirty ? colors.warningBackground : "transparent"};
  font-size: 14px;
  color: ${colors.text};

  &:hover:not(:disabled) {
    border-color: ${({ $dirty }) => ($dirty ? colors.warning : colors.border)};
  }

  &:focus:not(:disabled) {
    outline: none;
    border-color: ${colors.primary};
    background-color: ${colors.backgroundLight};
  }

  &:disabled {
    ${cellDisabledStyles}
  }
`;

export const ReadOnlyFieldset = styled.fieldset`
  display: contents;
`;

export const OverlayCellBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const OverrideMarker = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  cursor: help;

  &::after {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${colors.info};
  }
`;

export const OverrideResetButton = styled.button`
  display: none;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: ${colors.backgroundDark};
    color: ${colors.text};
  }
`;

export const OverlayCell = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;
  min-width: 0;

  &:hover ${OverrideResetButton}, &:focus-within ${OverrideResetButton} {
    display: inline-flex;
  }
`;

interface MoneyTableCellProps {
  amountMinor: number | null | undefined;
  dirty?: boolean;
  disabled?: boolean;
  allowEmpty?: boolean;
  placeholder?: string;
  idleText?: string;
  title?: string;
  onCommit: (amountMinor: number | null) => void;
}

export const MoneyTableCell = ({
  amountMinor,
  dirty,
  disabled = false,
  allowEmpty = false,
  placeholder,
  idleText,
  title,
  onCommit,
}: MoneyTableCellProps) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState("");

  const committedDisplay = formatMoneyAmountInputValue(amountMinor);
  const displayValue = focused ? draft : (idleText ?? committedDisplay);

  return (
    <Tooltip content={title} block>
      <CellInput
        type="text"
        inputMode="decimal"
        $dirty={dirty}
        disabled={disabled}
        placeholder={placeholder}
        value={displayValue}
        onFocus={() => {
          if (disabled) {
            return;
          }
          setFocused(true);
          setDraft(committedDisplay);
        }}
        onChange={(event) => {
          if (disabled) {
            return;
          }
          const next = event.target.value;
          if (isValidMoneyAmountInputDraft(next)) {
            setDraft(next);
          }
        }}
        onBlur={() => {
          if (disabled) {
            return;
          }
          setFocused(false);
          const trimmed = draft.trim();
          if (trimmed === "") {
            if (allowEmpty) {
              onCommit(null);
            }
            return;
          }
          const minor = moneyAmountInputToMinor(trimmed);
          if (minor !== null) {
            onCommit(minor);
          }
        }}
      />
    </Tooltip>
  );
};
